/* eslint-disable react-refresh/only-export-components */
/**
 * Shared subcomponents and types for the Industrialization (ICFA) pane.
 */
import React from 'react';
import type { DossierCostEstimate, IndustrializationDossier } from '../../services/industrializationService';

// ── Shared prop type for each step component ──────────────────────────────────

export interface StepProps {
  dossier: IndustrializationDossier | null;
  isRunning: boolean;
  costEstimate: DossierCostEstimate | null;
  showCostConfirm: boolean;
  onRequestGenerate: () => void;
  onConfirmGenerate: () => void;
  onCancelGenerate: () => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────

export const SEVERITY_COLORS: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
};

export const RISK_TYPE_LABELS: Record<string, string> = {
  critical: 'Critical',
  single_source: 'Single-source',
  long_lead: 'Long lead',
  hard_to_source: 'Hard to source',
};

export const MOB_COLORS: Record<string, string> = {
  make: 'text-emerald-700 font-medium',
  buy: 'text-blue-700 font-medium',
  undecided: 'text-slate-400',
};

// ── Atoms ─────────────────────────────────────────────────────────────────────

export const ToVerifyBadge: React.FC = () => (
  <span className="ml-1 inline-block px-1 py-0 text-[10px] rounded bg-amber-100 text-amber-700 border border-amber-300">
    to verify
  </span>
);

export const StepStatusDot: React.FC<{ status: string }> = ({ status }) => {
  const colors: Record<string, string> = {
    completed: 'bg-emerald-500',
    in_progress: 'bg-blue-400 animate-pulse',
    needs_revision: 'bg-amber-400',
    not_started: 'bg-slate-300',
  };
  return <span className={`inline-block w-2 h-2 rounded-full mr-1 ${colors[status] ?? 'bg-slate-300'}`} />;
};

// ── CostConfirmBar ────────────────────────────────────────────────────────────

interface CostConfirmBarProps {
  costEstimate: DossierCostEstimate | null;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const CostConfirmBar: React.FC<CostConfirmBarProps> = ({ costEstimate, confirmLabel, onConfirm, onCancel }) => (
  <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
    {costEstimate ? (
      <span>
        Estimated cost:{' '}
        <strong>
          {(costEstimate.estimated_cost_cents / 100).toFixed(3)} {costEstimate.currency}
        </strong>{' '}
        ({costEstimate.model_name ?? costEstimate.model_id})
      </span>
    ) : (
      <span className="text-slate-400">Estimating…</span>
    )}
    <span className="ml-3">
      <button
        onClick={onConfirm}
        disabled={!costEstimate}
        className="px-2 py-0.5 bg-blue-600 text-white rounded text-xs mr-1 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {confirmLabel}
      </button>
      <button onClick={onCancel} className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-xs hover:bg-slate-300">
        Cancel
      </button>
    </span>
  </div>
);

// ── GenerateButton ────────────────────────────────────────────────────────────

interface GenerateButtonProps {
  label: string;
  isRunning: boolean;
  onRequest: () => void;
  disabled?: boolean;
}

export const GenerateButton: React.FC<GenerateButtonProps> = ({ label, isRunning, onRequest, disabled }) => (
  <button
    onClick={onRequest}
    disabled={isRunning || disabled}
    className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
  >
    {isRunning && <span className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />}
    {label}
  </button>
);

// ── Field (label + value display) ─────────────────────────────────────────────

export const Field: React.FC<{ label: string; value: string | null | undefined; toVerify?: boolean }> = ({
  label,
  value,
  toVerify,
}) =>
  value ? (
    <div>
      <span className="font-medium text-slate-500">{label}: </span>
      {toVerify && <ToVerifyBadge />}
      <span className="text-slate-700 ml-1">{value}</span>
    </div>
  ) : null;
